import React      from 'react'
import {useTheme} from "@mui/material"
import SvgIcon    from "@mui/material/SvgIcon"

function BirdsyMiniMark({style, width, height, color}) {
	const theme = useTheme()
	const originalWidth = 396.98
	const originalHeight = 274.76
	const ratio = originalWidth / originalHeight

	const dim = width
		? {width: width, height: width / ratio}
		// was {width: width, ...} here too, so passing only `height` produced two undefineds
		: height
			? {width: height * ratio, height: height}
			: {width: "100%", height: "100%"}

	return (<SvgIcon style={{...style, ...dim}}>
		<svg viewBox={"0 0 " + originalWidth + " " + originalHeight}
		     width={dim.width}
		     height={dim.height}
		>
			<defs>
				{/*
				  * One class, one fill (133).
				  *
				  * This block was ported from bidsy-mark.svg with its selectors run together: the
				  * source has `.cls-1{fill:gradient-1}` and a SEPARATE `.cls-1,.cls-2,.cls-3
				  * {stroke-width:0px}` rule, and the port kept that three-class selector list but
				  * gave it gradient-2's fill. So the body (class 1) matched BOTH rules and the later
				  * one won.
				  *
				  * Gradient 2 spans x 245.6 -> 333.06, a narrow band at the right of a 397-wide
				  * artwork. Painting the whole bird with it clamped everything left of 245.6 to the
				  * first stop and everything right of 333 to the last: a flat magenta body with the
				  * purple confined to the front of the face, which is exactly what it looked like.
				  *
				  * Class 1 is the body and takes the full-width gradient. Class 2 is the small tail
				  * piece and keeps its own, as the source artwork has it. Class 3 is the white
				  * backing under that piece.
				  */}
				<style>{`
					.birdsy-mini-mark-1{fill:url(#birdsy-mini-mark-linear-gradient);}
					.birdsy-mini-mark-2{fill:url(#birdsy-mini-mark-linear-gradient-2);}
					.birdsy-mini-mark-3{fill:#fff;}
					.birdsy-mini-mark-1,
					.birdsy-mini-mark-2,
					.birdsy-mini-mark-3{stroke-width:0px;}
					`}
				</style>

				{/*
				  * gradientUnits="userSpaceOnUse" means these coordinates are in VIEWBOX space, not in
				  * rendered pixels — so they are the artwork's own dimensions, fixed, and not the
				  * width/height props.
				  *
				  * They used to read width/height directly. Every caller passes `width` alone (see
				  * BrandMark), so `height` was undefined and y1/y2 resolved to NaN, which is the
				  * "<linearGradient> attribute y1: Expected length" console error. The gradient fell
				  * back to a flat fill rather than failing visibly, which is why it went unnoticed.
				  */}
				<linearGradient id="birdsy-mini-mark-linear-gradient"
				                x1="0" y1={originalHeight / 2} x2={originalWidth} y2={originalHeight / 2}
				                gradientUnits="userSpaceOnUse">
					<stop offset="0" stopColor={color || "#d855ff"}/>
					<stop offset=".99" stopColor={color || "#7f30ff"}/>
				</linearGradient>
				{/* 245.6 is already a viewBox coordinate, like the 333.06 and 188.58 beside it — it
				    was being rescaled by the rendered width, which put it outside the artwork */}
				<linearGradient id="birdsy-mini-mark-linear-gradient-2"
				                x1="245.6" y1="188.58" x2="333.06" y2="188.58"
				                xlinkHref="#birdsy-mini-mark-linear-gradient"/>

			</defs>
			<g id="Layer_1-2">
				<path className="birdsy-mini-mark-3" d="M313.33,217.12c5.36,1.84,11.22,1.17,16.54-2.27,10.89-7.04,15.33-23.28,9.4-34.84-6.21-12.09-17.89-16.36-29.06-20.98-3.76,5.07-3.09,8.22,1.52,12.04,8.41,6.96,12.85,16.46,10.04,27.45-1.64,6.42-5.48,12.27-8.44,18.6Z"/>
				<path className="birdsy-mini-mark-1" d="M239.9,114.16c11.41-10.81,16.92-25.15,14.7-40.09-3.58-.34-7.55-.19-11.19-1.19-4.73-1.31-9.52-3-13.69-5.52-4.8-2.89-4.44-6.62.64-9.11,11.58-5.66,23.32-11,34.98-16.48.22-.1.32-.45.77-1.12-7.5-3.25-15.17-2.72-22.83-1.91-11.77,1.24-23.55,3.01-35.25-.45-7.12-2.11-13.75-5.17-18.73-10.88-4.82-5.52-3.92-9.7,2.97-11.95,15.81-5.16,31.75-9.95,47.66-14.79,5.47-1.67,10.35-.08,15.05,2.94,7.86,5.04,15.71,10.13,23.87,14.66,6.85,3.8,13.99,5.62,22.35,4.48,7.34-1,15.11.54,22.63,1.51,5.46.7,9.16,4.34,11.59,9.23.42.84.86,1.66,1.27,2.5,5.36,10.85,6.88,11.76,18.97,9.34,7.77-1.56,15.4-3.83,23.14-5.56,2.55-.57,5.26-.7,7.87-.57,1.77.09,4.76-.85,4.29,2.97-.05.37,1.14,1.28,1.78,1.29,5.17.12,4.38,3.87,3.85,6.82-3.33,18.47-11.65,33.86-28.42,43.45-7.01,4.01-14.34,7.56-21.81,10.62-16.68,6.84-27.29,19.17-33.54,35.67-2.04,5.38-3.76,10.88-5.66,16.32-.96,2.76-2.01,5.49-3.13,8.55-10.46-5.47-21.02-8.83-32.71-8.58,7.66,2.51,15.23,4.64,22.53,7.47,8.09,3.13,15.21,7.88,20.33,15.15,10.55,15,1.61,39.09-16.2,43.47-10.07,2.47-20.21-2.67-20.99-16.54-1.33.68-2.59,1.2-3.71,1.92-18.84,12.24-39.91,17.16-61.95,18.51-12.28.75-24.6.81-36.91.92-3.18.03-5.54.78-7.83,3.07-11.18,11.15-24.7,17.13-40.53,17.67-9.65.33-16.99,4.27-21.57,13.08-3.64,7.01-8.43,12.69-17.08,13.66-8.66.96-16.03-5.6-15.18-14.08.2-2.03,1.03-4.83,2.51-5.73,4.47-2.74,9.2-5.86,14.21-6.78,13.51-2.49,26.02-6.72,35.66-16.85,3.62-3.8,6.14-8.64,9.16-13-.4-.27-.8-.54-1.19-.81-2.5,2.91-4.92,5.89-7.51,8.72-11.49,12.52-25.98,18.35-42.75,19.02-1.4.06-2.81.17-4.21.06-8.19-.6-13.48,2.4-17.12,10.39-4.78,10.51-12.82,17.64-25.61,16.61-7.48-.6-13.87-5.68-15.77-12.59-1.59-5.8,1.25-12.62,6.85-14.16,5.5-1.51,7.74-5.3,9.52-9.92,1.56-4.07,2.87-8.24,4.6-12.23,3.86-8.91,7.53-10.91,17.13-9.33,6.03.99,10.96-1.2,14.52-5.65,5.31-6.63,10.03-13.73,14.54-19.98-11.17.19-22.41,1.33-33.47.36-23.51-2.06-41.11-13.6-50.65-35.75-1.79-4.16-2.45-9.01-2.66-13.58-.19-4.29,2.54-5.36,6.17-2.85,2.05,1.41,3.85,3.17,5.81,4.7,22.22,17.41,47.05,27.92,75.62,27.94,6.15,0,12.41-1.16,18.42-2.63,15.85-3.88,31.94-5.75,48.16-5.54,29.99.38,66.55-30.53,70.43-33.27s3.88-2.74,15.29-13.55ZM278.33,201.56c1.62-1.86,2.93-3.37,4.21-4.84-4.5-8.22-11.58-9.46-19.98-8.48,13.94,3.25,15.81,4.78,15.77,13.32Z"/>
				<path className="birdsy-mini-mark-2" d="M313.33,217.12c2.96-6.33,6.8-12.18,8.44-18.6,2.8-10.99-1.64-20.48-10.04-27.45-4.61-3.82-5.28-6.97-1.52-12.04,11.17,4.62,22.85,8.89,29.06,20.98,5.93,11.56,1.49,27.79-9.4,34.84-5.32,3.44-11.18,4.1-16.54,2.27Z"/>
			</g>
		</svg>
	</SvgIcon>)
}

export default BirdsyMiniMark
